import { useState } from 'react';
import type { GenplanPlot } from '../../model/types';
import { formatAreaSotka, formatOptionalPrice } from '../../lib/formatPlotValue';
import { getPlotPublicNumber } from '../../lib/getPlotPublicNumber';
import { getPlotStatusPresentation } from '../../lib/getPlotStatusPresentation';
import { analyticsGoals, trackAnalyticsGoal } from '../../../../lib/analytics/yandexMetrika';
import styles from './GenplanInfoPanel.module.scss';

interface GenplanInfoPanelProps {
  plot?: GenplanPlot;
}

export function GenplanInfoPanel({ plot }: GenplanInfoPanelProps) {
  return (
    <aside
      className={styles.panel}
      aria-live="polite"
      aria-atomic="true"
      aria-label="Информация об участке"
    >
      <div className={styles.transitionContent} key={plot?.id ?? 'empty'}>
        {plot ? <PlotDetails plot={plot} /> : <EmptyState />}
      </div>
    </aside>
  );
}

function EmptyState() {
  return (
    <>
      <p className={styles.eyebrow}>Информация</p>
      <p className={styles.placeholder}>
        Выберите участок на генплане, чтобы увидеть подробную информацию.
      </p>
    </>
  );
}

function PlotDetails({ plot }: { plot: GenplanPlot }) {
  const [shareMessage, setShareMessage] = useState('');
  const status = getPlotStatusPresentation(plot.status.kind);
  const price = formatOptionalPrice(plot);
  const plotFormat = getPlotFormat(plot);
  const plotNumber = getPlotPublicNumber(plot.displayNumber);

  const sharePlot = async () => {
    setShareMessage('');
    const url = getShareUrl(plotNumber);
    const shareData = {
      title: `Участок №${plotNumber} — Лучезарная Долина`,
      text: `Участок №${plotNumber}, ${formatAreaSotka(plot.areaSotka)}, ${status.label}.`,
      url,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        trackAnalyticsGoal(analyticsGoals.plotShareSuccess, {
          plot_number: plot.displayNumber,
        });
        return;
      } catch (error) {
        if (isShareCancellation(error)) return;
      }
    }

    const copied = await copyText(url);
    if (!copied) return;

    setShareMessage('Ссылка скопирована');
    trackAnalyticsGoal(analyticsGoals.plotLinkCopy, { plot_number: plot.displayNumber });
  };

  return (
    <>
      <p className={styles.eyebrow}>Выбранный участок</p>
      <div className={styles.heading}>
        <h3>Участок №{plotNumber}</h3>
        <span className={styles.status} data-tone={status.tone}>
          {status.label}
        </span>
      </div>

      <dl className={styles.details}>
        <div>
          <dt>Кадастровый номер</dt>
          <dd>{plot.cadastralNumber}</dd>
        </div>
        <div>
          <dt>Площадь</dt>
          <dd>{formatAreaSotka(plot.areaSotka)}</dd>
        </div>
        {price ? (
          <div>
            <dt>Стоимость</dt>
            <dd>{price}</dd>
          </div>
        ) : null}
        {plotFormat ? (
          <div>
            <dt>Формат</dt>
            <dd>{plotFormat}</dd>
          </div>
        ) : null}
      </dl>

      <div className={styles.actions}>
        <a
          className={styles.callAction}
          href="tel:+79956003109"
          onClick={() =>
            trackAnalyticsGoal(analyticsGoals.plotCallClick, {
              plot_number: plot.displayNumber,
            })
          }
        >
          Позвонить
        </a>
        <button className={styles.shareAction} type="button" onClick={() => void sharePlot()}>
          Поделиться
        </button>
      </div>
      <p className={styles.shareMessage} aria-live="polite">
        {shareMessage}
      </p>
    </>
  );
}

function isShareCancellation(error: unknown) {
  return Boolean(
    error && typeof error === 'object' && 'name' in error && error.name === 'AbortError',
  );
}

function getPlotFormat(plot: GenplanPlot) {
  if (plot.current)
    return plot.current.formatNeedsReview
      ? undefined
      : plot.current.format === 'contractor'
        ? 'Зона единой архитектуры'
        : 'Без подряда';
  if (plot.status.kind === 'price-per-sotka-with-contractor') return 'Зона единой архитектуры';
  if (plot.status.kind === 'price-per-sotka' || plot.status.kind === 'free') return 'Без подряда';
  return undefined;
}

function getShareUrl(displayNumber: string) {
  const url = new URL(window.location.href);
  url.searchParams.set('plot', displayNumber);
  url.hash = '';
  return url.toString();
}

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Continue with the selection-based fallback.
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = value;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.append(textArea);
  textArea.select();

  try {
    return document.execCommand?.('copy') ?? false;
  } catch {
    return false;
  } finally {
    textArea.remove();
  }
}
